import { useState, useEffect } from 'react';
import { getProdSupabaseClient } from '@/lib/supabase/client';
import { translateSystemError, isSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import { useCurrentTenantId } from '@/contexts/TenantContext';

interface UserPreferencesRecord {
  updated_at: string;
}

export const useLastRefresh = (
  type: 'recall-data' | 'user-preferences' | 'technicians' = 'recall-data'
) => {
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const tenantId = useCurrentTenantId();

  useEffect(() => {
    const fetchLastRefresh = async () => {
      try {
        setLoading(true);
        setError(null);

        if (type === 'technicians') {
          // Get the maximum updated_at from user_preferences for technician management screen only
          // Use prod client (already configured for prod schema)
          const prodClient = getProdSupabaseClient();
          const result = await prodClient
            .from('user_preferences')
            .select('updated_at')
            .eq('preference_type', 'technician_management')
            .order('updated_at', { ascending: false })
            .limit(1);

          if (result.error) {
            throw result.error;
          }

          const data = (result.data as UserPreferencesRecord[]) || [];
          if (data.length > 0 && data[0]?.updated_at) {
            setLastRefresh(new Date(data[0].updated_at));
          } else {
            setLastRefresh(null);
          }
        } else if (type === 'user-preferences') {
          // Get the maximum updated_at from user_preferences for config screens (campaigns, technicians, dealers)
          // Use prod client (already configured for prod schema)
          const prodClient = getProdSupabaseClient();
          const result = await prodClient
            .from('user_preferences')
            .select('updated_at')
            .order('updated_at', { ascending: false })
            .limit(1);

          if (result.error) {
            throw result.error;
          }

          const data = (result.data as UserPreferencesRecord[]) || [];
          if (data.length > 0 && data[0]?.updated_at) {
            setLastRefresh(new Date(data[0].updated_at));
          } else {
            setLastRefresh(null);
          }
        } else {
          // Get the MAX snapshot_date for this tenant using dedicated RPC
          if (!tenantId) {
            // Tenant context is still loading, wait for it
            setLoading(true);
            return;
          }

          const prodClient = getProdSupabaseClient();
          const { data, error: rpcError } = await prodClient.rpc(
            'get_max_snapshot_date_for_tenant',
            { p_tenant_id: tenantId }
          );

          if (rpcError) {
            throw rpcError;
          }

          if (data) {
            // data is a date string (YYYY-MM-DD), parse as local date to avoid timezone issues
            setLastRefresh(new Date(data + 'T00:00:00'));
          } else {
            setLastRefresh(null);
          }
        }
      } catch (err) {
        // Error fetching last update time - handled by error state
        const source =
          type === 'user-preferences'
            ? 'user preferences'
            : type === 'technicians'
              ? 'technician management'
              : 'materialized view';
        const context = `fetching last update time from ${source}`;
        logSystemError(err, context);
        const errorMessage = isSystemError(err)
          ? translateSystemError(err, context).message
          : err instanceof Error
            ? err.message
            : `Failed to fetch last update time from ${source}`;
        setError(errorMessage);
        // Don't show toast for last refresh errors - they're not critical user actions
        setLastRefresh(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLastRefresh();
  }, [type, tenantId]);

  return {
    lastRefresh,
    loading,
    error
  };
};
